import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cardList } from './miner-card-data';
import { useTranslation } from 'react-i18next';

type CardListKey = keyof (typeof cardList)[0] | 'revenue' | 'revenueUSD';

interface MinerRevenueSectionProps {
  cardWithHashrate: Array<{
    id: number;
    cuda_type: string;
    hash_rate: number;
    revenue: string;
    revenueUSD: string;
  }>;
  selectedId: number | null;
  searchValue: string;
}

const highlightText = (text: string, searchValue: string) => {
  if (!searchValue) return text;
  const regex = new RegExp(`(${searchValue})`, 'gi');
  return text.replace(regex, '<span style="background-color: yellow;">$1</span>');
};

export const MinerRevenueSection = ({ cardWithHashrate, selectedId, searchValue }: MinerRevenueSectionProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const rowRefs = useRef<{ [key: number]: HTMLTableRowElement | null }>({});
  const { t } = useTranslation();
  const tableHeader: Array<{ label: string; key: CardListKey }> = [
    { label: t('minerRevenueSectionPage.cardType'), key: 'cuda_type' },
    { label: `${t('minerRevenueSectionPage.hashRate')} (Mh/s)`, key: 'hash_rate' },
    { label: `ABEL ${t('minerRevenueSectionPage.ABELRevenue')} (24h)`, key: 'revenue' },
    { label: `USD ${t('minerRevenueSectionPage.ABELRevenue')} (24h)`, key: 'revenueUSD' },
  ];

  useEffect(() => {
    if (selectedId !== null) {
      const index = cardWithHashrate.findIndex((item) => item.id === selectedId);
      if (index !== -1) {
        const selectedPage = Math.floor(index / rowsPerPage);
        setPage(selectedPage);
        setHighlightedId(selectedId);
      }
      return;
    }
  }, [selectedId, cardWithHashrate, rowsPerPage]);

  useEffect(() => {
    if (highlightedId !== null) {
      const timer = setTimeout(() => setHighlightedId(null), 1000);
      setTimeout(() => {
        rowRefs.current[highlightedId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [highlightedId]);

  const paginatedCardList = useMemo(() => {
    return cardWithHashrate.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, cardWithHashrate]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  return (
    <Card>
      <CardContent sx={{ overflow: 'auto' }}>
        <TableContainer sx={{ minHeight: '320px', overflow: 'auto' }}>
          <Table aria-label="simple table" sx={{ minWidth: '500px' }}>
            <TableHead>
              <TableRow>
                {tableHeader.map(({ label, key }) => (
                  <TableCell key={key}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCardList.map((row) => (
                <TableRow
                  key={row.id}
                  ref={(el) => (rowRefs.current[row.id] = el)}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    transition: row.id === highlightedId ? 'background-color 1s ease-out' : 'none',
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    dangerouslySetInnerHTML={{ __html: highlightText(row.cuda_type, searchValue) }}
                  />

                  <TableCell>{row.hash_rate}</TableCell>
                  <TableCell>{row.revenue} ABEL</TableCell>
                  <TableCell>≈ {row.revenueUSD} USD</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ pl: 0 }}
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={cardWithHashrate.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          labelRowsPerPage={t('global.rowsPerPage')}
          onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
        />
      </CardContent>
    </Card>
  );
};
